import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_REASON_CODE_MAPPING,
  DELETE_ALL_MASTER_DATA_REASON_CODE_MAPPINGS,
  DELETE_MASTER_DATA_REASON_CODE_MAPPINGS,
  GET_MASTER_DATA_REASON_CODE_MAPPINGS,
  UPDATE_MASTER_DATA_REASON_CODE_MAPPING,
} from "@src/domain/graphql/masterData";
import {
  MasterDataReasonCodeMapping,
  type IMasterDataReasonCodeMapping,
  type IMasterDataReasonCodeMappingDeleteResult,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataReasonCodeMappingDTO,
  DeleteAllMasterDataReasonCodeMappingsDTO,
  DeleteMasterDataReasonCodeMappingsDTO,
  GetMasterDataReasonCodeMappingsDTO,
  UpdateMasterDataReasonCodeMappingDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataReasonCodeMappingRepository,
  MasterDataReasonCodeMappingsListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapMappingItem(
  item: IMasterDataReasonCodeMapping,
): MasterDataReasonCodeMapping {
  return MasterDataReasonCodeMapping.from({
    mappingId: item.mappingId,
    purchaserId: item.purchaserId,
    purchaserName: item.purchaserName ?? null,
    purchaserKey: item.purchaserKey ?? null,
    reasonCode: item.reasonCode,
    reasonCodeKey: item.reasonCodeKey,
    deductionTypeId: item.deductionTypeId,
    deductionTypeName: item.deductionTypeName ?? null,
    deductionTypeKey: item.deductionTypeKey ?? null,
    promoTypeId: item.promoTypeId ?? null,
    promoTypeName: item.promoTypeName ?? null,
    createdAt: item.createdAt ?? null,
    createdBy: item.createdBy ?? null,
    updatedAt: item.updatedAt ?? null,
    updatedBy: item.updatedBy ?? null,
  });
}

function toMappingMutationParams(
  params:
    | CreateMasterDataReasonCodeMappingDTO
    | UpdateMasterDataReasonCodeMappingDTO,
) {
  return {
    purchaserId: params.purchaserId,
    deductionTypeId: params.deductionTypeId,
    reasonCode: params.reasonCode,
    reasonCodeKey: params.reasonCodeKey,
    promoTypeId: params.promoTypeId ?? null,
  };
}

function mapDeleteResult(
  payload: IMasterDataReasonCodeMappingDeleteResult,
): IMasterDataReasonCodeMappingDeleteResult {
  return {
    deletedCount: payload.deletedCount ?? 0,
    blockedCount: payload.blockedCount ?? 0,
    blockedIds: payload.blockedIds ?? [],
    message: payload.message ?? null,
  };
}

export class MasterDataReasonCodeMappingRepository
  implements IMasterDataReasonCodeMappingRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getReasonCodeMappings(
    params: GetMasterDataReasonCodeMappingsDTO,
  ): Promise<MasterDataReasonCodeMappingsListResult> {
    const { data, error } = await this.client.query<{
      masterDataReasonCodeMappings: {
        items: IMasterDataReasonCodeMapping[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_REASON_CODE_MAPPINGS,
      variables: {
        params: {
          search: params.search ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataReasonCodeMappings;
    if (!response) {
      throw new Error("Failed to fetch master data reason code mappings");
    }

    return toMasterDataListResult(
      response,
      mapMappingItem,
      params.page_number,
      params.page_size,
    );
  }

  async createReasonCodeMapping(
    params: CreateMasterDataReasonCodeMappingDTO,
  ): Promise<MasterDataReasonCodeMapping> {
    return this.putReasonCodeMapping(
      CREATE_MASTER_DATA_REASON_CODE_MAPPING,
      toMappingMutationParams(params),
      (data) => data?.createMasterDataReasonCodeMapping,
      "Failed to create reason code mapping",
    );
  }

  async updateReasonCodeMapping(
    params: UpdateMasterDataReasonCodeMappingDTO,
  ): Promise<MasterDataReasonCodeMapping> {
    return this.putReasonCodeMapping(
      UPDATE_MASTER_DATA_REASON_CODE_MAPPING,
      {
        mappingId: params.mappingId,
        ...toMappingMutationParams(params),
      },
      (data) => data?.updateMasterDataReasonCodeMapping,
      "Failed to update reason code mapping",
    );
  }

  async deleteReasonCodeMappings(
    params: DeleteMasterDataReasonCodeMappingsDTO,
  ): Promise<IMasterDataReasonCodeMappingDeleteResult> {
    const result = await this.client.mutate<{
      deleteMasterDataReasonCodeMappings?: IMasterDataReasonCodeMappingDeleteResult;
    }>({
      mutation: DELETE_MASTER_DATA_REASON_CODE_MAPPINGS,
      variables: {
        params: {
          mappingIds: params.mappingIds,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapDeleteResult(
      requireMasterDataMutationData(
        result.data?.deleteMasterDataReasonCodeMappings,
        result.error,
        "Failed to delete reason code mappings",
      ),
    );
  }

  async deleteAllReasonCodeMappings(
    _params: DeleteAllMasterDataReasonCodeMappingsDTO,
  ): Promise<IMasterDataReasonCodeMappingDeleteResult> {
    const result = await this.client.mutate<{
      deleteAllMasterDataReasonCodeMappings?: IMasterDataReasonCodeMappingDeleteResult;
    }>({
      mutation: DELETE_ALL_MASTER_DATA_REASON_CODE_MAPPINGS,
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapDeleteResult(
      requireMasterDataMutationData(
        result.data?.deleteAllMasterDataReasonCodeMappings,
        result.error,
        "Failed to delete reason code mappings",
      ),
    );
  }

  private async putReasonCodeMapping(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataReasonCodeMapping?: IMasterDataReasonCodeMapping;
            updateMasterDataReasonCodeMapping?: IMasterDataReasonCodeMapping;
          }
        | null
        | undefined,
    ) => IMasterDataReasonCodeMapping | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataReasonCodeMapping> {
    const result = await this.client.mutate<{
      createMasterDataReasonCodeMapping?: IMasterDataReasonCodeMapping;
      updateMasterDataReasonCodeMapping?: IMasterDataReasonCodeMapping;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapMappingItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
